import { ElementRef, useRef } from 'react';

import { Avatar, Box, Flex, Heading, Hide, Text, useMediaQuery, useQuery } from '@chakra-ui/react';
import ArrowIcon from 'images/icons/arrow-right.svg';

import { Button } from '@/components/atoms';
import { Slider } from '@/components/functional';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { DynamicYourTurnSectionFragment } from '@/graphql/generated';
import { GeneralPath } from '@/utils/models';

import { SliderControls } from '../../../functional/SliderControls/SliderControls';
import { Card } from './Card';

export const DynamicYourTurnSection = ({
  header,
  subheader,
  paragraph,
  quote,
  person,
  position,
  photo,
  leaders = { data: [] },
}: DynamicYourTurnSectionFragment) => {
  const containerRef = useRef(null);
  const [isAboveXS] = useMediaQuery(useQuery({ above: 'xs' }));
  const sliderRef = useRef<ElementRef<typeof Slider>>(null);

  return (
    <SectionWrapper>
      <SectionContentWrapper ref={containerRef} marginBottom={20}>
        <Flex direction={{ base: 'column' }} gap={{ base: 10, md: 20 }}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 10, md: 20 }}
            align="center"
            justifyContent="space-between"
          >
            {/* Left column */}
            <Flex direction="column" align="flex-start" gap={6} maxWidth={{ md: '588px' }}>
              <Heading
                variant={{
                  base: 'component-heading-large/xl',
                  sm: 'component-heading-large/3xl',
                }}
                color="purple.500"
              >
                {header}
              </Heading>

              <Text
                variant={{
                  base: 'text-xl/lineHeight-7/font-normal',
                }}
                color="purple.500"
              >
                {subheader}
              </Text>

              <Text
                color="purple.400"
                variant={{
                  base: 'text-md/lineHeight-6/font-normal',
                }}
              >
                {paragraph}
              </Text>

              <Button
                variant="light"
                label="Let's talk"
                rightIcon={<ArrowIcon width="12px" />}
                mt={{ base: 8 }}
                w={{ sm: '178px' }}
                url={GeneralPath.CONTACT}
                size="lg"
              />
            </Flex>

            {/* Right column */}
            <Flex direction="column" px={{ md: 6 }} py={{ md: 4 }} gap={6} maxWidth={{ md: '588px' }}>
              <Text
                color="purple.400"
                variant={{
                  base: 'text-md/lineHeight-6/font-normal',
                }}
              >
                {quote}
              </Text>
              <Flex align="center">
                {photo?.data?.attributes && <Avatar src={photo.data.attributes.url} name={person || ''} mr={3} />}
                <Box>
                  <Text
                    color="purple.500"
                    variant={{
                      base: 'text-md/lineHeight-6/font-semibold',
                    }}
                  >
                    {person}
                  </Text>
                  <Text
                    color="purple.400"
                    variant={{
                      base: 'text-xs/lineHeight-4/font-normal/0.75rem',
                    }}
                  >
                    {position}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Flex>

          <Flex flexDir="column" gap={4}>
            <Hide above="3xl">
              <SliderControls
                onSlidePrev={() => sliderRef.current?.onPrev()}
                onSlideNext={() => sliderRef.current?.onNext()}
                alignSelf="start"
                justifySelf={{ base: 'self-start' }}
                variant="light"
              />
            </Hide>
            <Slider ref={sliderRef} snapDirection={isAboveXS ? 'start' : 'center'} pt="5px" gapPX={24}>
              {leaders.data.map((leader, index) => leader && <Card key={`leader-item-${index}`} {...leader} />)}
            </Slider>
          </Flex>
        </Flex>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
